import { create } from 'zustand';

import type { AdminRole } from '@delegation/contracts';

export type AuthRole = 'admin' | 'delegated';

export interface AuthUser {
  id: string;
  role: AuthRole;
  fullName: string;
  /// Admin-only: the granular RBAC role (SUPER_ADMIN, REVIEWER, …). Drives
  /// in-app gating like the admin-management screen. Undefined for delegates.
  adminRole?: AdminRole;
  /// Delegate-only: the units they manage (multi-unit) + whether a forced
  /// password change is pending. Undefined for admins.
  units?: {
    id: string;
    unitNumber: string;
    status: string;
    hasPermit: boolean;
    /// The delegate's effective authorization window for this unit (intersection
    /// of account + unit-link validity). Null = open on that side.
    validFrom?: string | null;
    validTo?: string | null;
  }[];
  mustChangePassword?: boolean;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setSession: (accessToken: string, user: AuthUser) => void;
  setAccessToken: (accessToken: string | null) => void;
  /// Shallow-merge a patch into the current user (e.g. clear the
  /// must-change-password flag after a successful change).
  updateUser: (patch: Partial<AuthUser>) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  setSession: (accessToken, user) => set({ accessToken, user, isAuthenticated: true }),
  setAccessToken: (accessToken) =>
    set((state) => ({ accessToken, isAuthenticated: !!accessToken && !!state.user })),
  updateUser: (patch) =>
    set((state) => ({ user: state.user ? { ...state.user, ...patch } : null })),
  clear: () => set({ accessToken: null, user: null, isAuthenticated: false }),
}));

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function clearAuth(): void {
  useAuthStore.getState().clear();
}
